"use client";

import { Button } from "@/components/atoms/buttons";
import ModalTemplate from "@/components/molecules/modal-template";
import { DialogClose } from "@/components/ui/dialog";
import { useCustomer } from "@/hooks/useCustomer";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { markVoluntaryLogout } from "@/lib/auth/logout-intent";
import { sdk } from "@/lib/medusa/client";
import { cn, getHomePageSlug } from "@/lib/utils";
import { useCheckoutStore } from "@/stores/checkout-store";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight, LogOut } from "lucide-react";
import { useRouter } from "next/navigation";

type LogoutCustomerProps = {
  locale: Locales;
  isInNavbar?: boolean;
};

const LogoutCustomer = ({ locale, isInNavbar }: LogoutCustomerProps) => {
  const route = useRouter();
  const { setCustomer } = useCustomer();
  const queryClient = useQueryClient();

  const handleLogout = async () => {
    try {
      await sdk.auth.logout();
      markVoluntaryLogout();
      setCustomer(null);
      useCheckoutStore.getState().resetFormData();
      queryClient.removeQueries({ queryKey: ["company-details"] });
      queryClient.removeQueries({ queryKey: ["company-employees"] });
      queryClient.removeQueries({ queryKey: ["company-addresses"] });
      window.sessionStorage.removeItem("invitation_data");
      route.push(getHomePageSlug(locale));
    } catch (error) {
      console.error("Logout failed:", error);
      return;
    }
  };

  return (
    <ModalTemplate
      classNameTitle="font-cabinet-grotesk"
      title={__("Confirm Logout", locale)}
      triggerAsChild
      modalTrigger={
        <button
          className={cn(
            "group hover:text-dark-primary flex w-full cursor-pointer items-center justify-between rounded-sm px-2.5 py-3 font-semibold transition-all duration-200 ease-in-out hover:bg-[#fafaf9]",
            isInNavbar && "py-1.5"
          )}
        >
          <span className="flex items-center gap-2">
            <LogOut aria-hidden="true" size={20} strokeWidth={1.8} />
            {__("Log out", locale)}
          </span>
          <ChevronRight
            aria-hidden="true"
            className={cn("hidden group-focus-visible:block group-active:block", isInNavbar && "block")}
            size={24}
          />
        </button>
      }
    >
      <div className="mx-5 space-y-5 border-t pt-5 pb-5.5">
        <p>{__("Are you sure you want to log out?", locale)}</p>
        <div className="flex items-center justify-end gap-2.5">
          <DialogClose asChild>
            <Button variant="secondary" className="h-11" size="md">
              {__("Cancel", locale)}
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button size="md" className="h-11" onClick={handleLogout} variant="danger">
              {__("Log out", locale)}
            </Button>
          </DialogClose>
        </div>
      </div>
    </ModalTemplate>
  );
};

export default LogoutCustomer;
